import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useBreadcrumbsData } from '../breadcrumbsContext/useBreadcrumbsData/useBreadcrumbsData'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { SET_LOGOUT } from '@percent/admin-dashboard/context/auth/authReducer/authReducer'
import { logger } from '@percent/admin-dashboard/common/utility/logger'
import { useAuthDispatch, useAuthState } from '@percent/admin-dashboard/common/hooks'
import { ReactComponent as DropdownSVG } from '@percent/admin-dashboard/common/assets/images/dropdown.svg'
import styles from './TopBar.module.scss'
import { Flex, Breadcrumbs, Menu, Typography, SectionItem, SectionProps } from '@percent/lemonade'
import { resolveBreadcrumbs } from './resolveBreadcrumbs'

export function TopBar() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { authService } = useServices()
  const dispatch = useAuthDispatch()
  const { user } = useAuthState()
  const { pathname } = useLocation()
  const { path: locationPath } = useBreadcrumbsData()

  const breadcrumbItems = useMemo(() => resolveBreadcrumbs(pathname, locationPath), [pathname, locationPath])

  const handleLogout = useCallback(() => {
    // Signed out here even when the server's logout fails, so the tab never keeps looking signed in
    authService
      .logout()
      .catch(error => logger(error))
      .then(() => {
        dispatch({
          type: SET_LOGOUT
        })
        navigate('/login')
      })
  }, [authService, dispatch, navigate])

  const menuSections = useMemo<SectionProps[]>(() => {
    const sectionItems: SectionItem[] = [
      {
        label: t('menu.accountSettings'),
        key: 'myAccount',
        as: 'link',
        to: '/my-account',
        icon: 'settings'
      },
      {
        label: t('menu.logout'),
        key: 'logOut',
        as: 'button',
        onClick: handleLogout,
        icon: 'log-out',
        dataTestId: 'navigation-bar-logout-button'
      }
    ]

    return [{ items: sectionItems }]
  }, [handleLogout, t])

  return (
    <div className={styles.wrapper}>
      <Flex align="center" px={24} justify="space-between" w="100%" className={styles.topBar}>
        <Breadcrumbs items={breadcrumbItems} aria-label="breadcrumb" />
        <Flex direction="row-reverse" justify="center" align="center">
          <Menu
            sections={menuSections}
            customTriggerComponent={
              <div className={styles.dropdown} data-testid="topbar-menu-trigger">
                <Menu.Item className={styles.menuItem}>
                  <div className={styles.userDetail} data-testid="navigation-bar-user-details">
                    <Typography className={styles.nameText}>
                      {user?.fullName ?? user?.preferredName ?? user?.email}
                    </Typography>
                    <Typography className={styles.roleText} variant="small">
                      <span data-testid="user-role">Admin</span>
                    </Typography>
                  </div>
                  <DropdownSVG className={styles.icon} />
                </Menu.Item>
              </div>
            }
          />
        </Flex>
      </Flex>
    </div>
  )
}
